import { Injectable } from '@angular/core';
import { AuthService, AuthUser, Roles } from '@core';
import { TypedQuery } from '@data';
import { AccountUser, JobOrder } from '@models';
import { AccountDbQueryService } from './account-db-query.service';

/**
 * Single-authority answer to "give me a query already scoped to the current admin's authority."
 *
 * Encapsulates the GlobalAdmin / Principal Account Admin / restricted-admin decision that would
 * otherwise be re-implemented per component. Every scoped list in the client MUST route through
 * one of the createScoped*Query methods below — the raw AccountDbQueryService queries
 * (createAccountUsersQuery, createJobOrdersQuery, createAccountUsersForAccountAdminIdQuery,
 * createJobOrdersByAccountAdminIdQuery, createAllAccountUsersQuery) remain public but should
 * only be used INSIDE this service.
 *
 * Mirrors the server-side ApprovalTreeAuthorizationService.IsUnscoped contract: a GlobalAdmin
 * or Principal Account Admin sees the full account (or all accounts, for GlobalAdmin); every
 * other admin type sees only AccountUsers/JobOrders reachable via their Organization Tree
 * position. See Dashboard-Org-Scoped-Filtering design §4.4.
 */
@Injectable({ providedIn: 'root' })
export class AdminScopeService {
  constructor(
    private readonly authService: AuthService,
    private readonly dbQueryService: AccountDbQueryService,
  ) {}

  /**
   * True iff the current user has cross-Organization-Tree visibility — GlobalAdmin or Principal
   * Account Admin. Mirrors the server-side ApprovalTreeAuthorizationService.IsUnscoped rule.
   * Use for *ngIf gates that should hide "show only mine" toggles or scope-picker widgets from
   * users who legitimately see everything.
   */
  get isUnscoped(): boolean {
    const u = this.authUser;
    if (u == null) return false;
    return u.isGlobalAdmin || u.hasRole(Roles.PrincipalAccountAdmin);
  }

  /**
   * Give me an AccountUser query scoped to the current admin's visibility.
   *   - GlobalAdmin without accountId argument: cross-account (every user, every account).
   *   - GlobalAdmin with accountId argument: single account, all users on it.
   *   - PAA: all users on their account (or on the requested account, if provided; caller is
   *     assumed to have authorization to view that account via other means).
   *   - Restricted admin: users reachable via the admin's Organization Tree position.
   *
   * The optional accountId lets a screen pin the query to a specific account when the user is
   * viewing another account's data (e.g., a PAA browsing a different account after
   * account-switch). Omit it for "my own account" screens.
   */
  createScopedAccountUsersQuery(accountId?: string): TypedQuery<AccountUser> {
    const u = this.requireAuthUser();
    if (u.isGlobalAdmin && accountId == null) {
      return this.dbQueryService.createAllAccountUsersQuery();
    }
    if (u.isGlobalAdmin || u.hasRole(Roles.PrincipalAccountAdmin)) {
      const effectiveAccountId = accountId ?? u.accountId!;
      return this.dbQueryService.createAllAccountUsersQuery().where({ accountId: effectiveAccountId });
    }
    if (u.accountAdminId == null) {
      // Restricted admin without an AccountAdmin row is a misconfiguration; return an empty
      // set rather than leak all users. Server-side query would reject anyway.
      return this.dbQueryService.createAllAccountUsersQuery().where({ id: null });
    }
    return this.dbQueryService.createAccountUsersForAccountAdminIdQuery(u.accountAdminId, true);
  }

  /**
   * Give me a JobOrder query scoped to the current admin's visibility.
   *   - GlobalAdmin without accountId argument: cross-account.
   *   - GlobalAdmin with accountId: pinned to that account.
   *   - PAA: pinned to their account (or the requested account).
   *   - Restricted admin: JobOrders reachable via the admin's Organization Tree position
   *     (hierarchical — every admin at or above the JO's PUG per Q1 rule).
   */
  createScopedJobOrdersQuery(accountId?: string): TypedQuery<JobOrder> {
    const u = this.requireAuthUser();
    if (u.isGlobalAdmin && accountId == null) {
      return this.dbQueryService.createJobOrdersQuery();
    }
    if (u.isGlobalAdmin || u.hasRole(Roles.PrincipalAccountAdmin)) {
      const effectiveAccountId = accountId ?? u.accountId!;
      return this.dbQueryService.createJobOrdersQuery().where({ accountId: effectiveAccountId });
    }
    if (u.accountAdminId == null) {
      return this.dbQueryService.createJobOrdersQuery().where({ id: null });
    }
    return this.dbQueryService.createJobOrdersByAccountAdminIdQuery(u.accountAdminId, true);
  }

  private get authUser(): AuthUser | undefined {
    return this.authService.getUser();
  }

  private requireAuthUser(): AuthUser {
    const u = this.authUser;
    if (u == null) throw new Error('AdminScopeService called without an authenticated user');
    return u;
  }
}
